import { Box, Button, HStack, Icon, Spinner, Stack, Text } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiBell } from "react-icons/fi";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  CompetitionStageDto,
  PortalLifecycleStatus,
  useGetPortalRegistrationStatusQuery,
  useRegisterPortalInterestMutation,
  type PortalSourcingEventDetailDto,
} from "../../../../autogen/bff-api";
import { currentPathSuffix } from "../../../../common/auth/returnTo";
import { useApiError } from "../../../../common/errors/useApiError";
import { useAppSelector } from "../../../../common/redux/hooks";
import { urls } from "../../../../urls";
import { momentLocaleFor } from "../../../../common/formatting/i18nFormatters";
import { formatShortDate } from "../utils/displayFormatters";
import { isDpsCompetition, isTwoStageProcedure } from "../../buyer/wizard/utils/procedure";
import { isPrivatePortalEvent } from "./PrivateInviteCta";
import { isNegotiationWithoutNotice } from "../../buyer/wizard/utils/negotiationWithoutNotice";

export const RegisterInterestCta = ({ event }: { event: PortalSourcingEventDetailDto }) => {
  const { t, i18n } = useTranslation();
  const authState = useAppSelector((state) => state.authState.state);
  const location = useLocation();
  const loggedInWithOrg = authState?.type === "LoggedInWithOrgContext";
  const isOpen = event.lifecycle.status === PortalLifecycleStatus.Open;
  const displayer = useApiError();
  const [register, { isLoading }] = useRegisterPortalInterestMutation();
  const [justRegistered, setJustRegistered] = useState(false);
  // Server is the source of truth so the "following" state survives refresh/refetch.
  // Mirrors the route's own guard, which 409s on two-stage and 404s on anything not publicly
  // visible. Keyed on the procedure: a system past its validity has no stage to key on.
  const takesInterest = event.competitionStage === CompetitionStageDto.InterestOpen;
  const entersByInvitation =
    (isTwoStageProcedure(event.procedureType) ||
      isDpsCompetition(event.procedureType) ||
      isNegotiationWithoutNotice(event.procedureType)) &&
    !takesInterest;
  const { data: status, isLoading: statusLoading } = useGetPortalRegistrationStatusQuery(
    { eventId: event.id },
    { skip: !loggedInWithOrg || !isOpen || entersByInvitation || isPrivatePortalEvent(event) }
  );

  // Only an open competition can be followed; the lifecycle banner explains other states.
  // Everything entered by invitation has its own surface: ParticipationCta, StartBidCta or
  // PrivateInviteCta.
  if (!isOpen || entersByInvitation || isPrivatePortalEvent(event)) return null;

  const registered = justRegistered || (status?.hasRegistered ?? false);

  const handleRegister = async () => {
    try {
      await register({ eventId: event.id }).unwrap();
      setJustRegistered(true);
    } catch (error) {
      displayer.trigger(error);
    }
  };

  return (
    <Box borderWidth="1px" borderColor="smBorder" bg="smBackground" rounded="xl" px={{ base: "4", md: "6" }} py="4">
      <HStack spacing="4" justify="space-between" align="center" flexWrap="wrap" rowGap="3">
        <HStack spacing="3" align="center">
          <Icon as={FiBell} boxSize="5" color="smPrimary" flexShrink="0" />
          <Stack spacing="0">
            <Text fontWeight="semibold">{t("Want to participate in this procurement?")}</Text>
            <Text fontSize="sm" color="smMuted">
              {takesInterest
                ? t(
                    "Register interest on this prior information notice. The buyer invites the suppliers who registered to confirm their interest, planned for {{date}}.",
                    { date: formatShortDate(event.plannedInterestInvitationAt ?? "", momentLocaleFor(i18n.language)) },
                  )
                : t("Register interest to follow updates from the buyer.")}
            </Text>
          </Stack>
        </HStack>
        {!loggedInWithOrg ? (
          <Button as={RouterLink} to={urls.loginWithReturnUrl(currentPathSuffix(location))} variant="outline" colorScheme="gray" flexShrink="0">
            {t("Log in to register interest")}
          </Button>
        ) : statusLoading && !justRegistered ? (
          <Spinner size="sm" color="smPrimary" flexShrink="0" />
        ) : registered ? (
          <Text fontWeight="semibold" color="smPrimary" flexShrink="0">
            {takesInterest
              ? t("You have registered interest. You will be told when the buyer invites you to confirm it.")
              : t("You are following this procurement")}
          </Text>
        ) : (
          <Button
            bg="smPrimary"
            color="white"
            _hover={{ opacity: 0.9 }}
            onClick={handleRegister}
            isLoading={isLoading}
            flexShrink="0"
          >
            {t("Register interest")}
          </Button>
        )}
      </HStack>
    </Box>
  );
};
